import { Hammer, X, ChevronDown, ChevronRight } from 'lucide-react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import LogoSection from './LogoSection';
import { UserContext } from '../../../context/user/UserContext';
import { CraftContext } from '../../../context/crafts/CraftContext';
import { ChatContext } from '../../../context/chat/ChatContext';
import SwitchRoleToggle from './SwitchRoleToggle';

export default function MobileMenu({
  isMenuOpen,
  setIsMenuOpen,
  loggedIn,
  userDetail,
  isDualRole,
  handleUpgradeToArtisan,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { categorizedCrafts } = useContext(CraftContext);
  const { unreadMsgCount } = useContext(ChatContext);
  const [isCraftsOpen, setIsCraftsOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);

  const openSignup = () => {
    setIsMenuOpen(false);
    navigate('/signup', { state: { background: location } });
  };

  const openJoinArtisan = () => {
    setIsMenuOpen(false);
    navigate('/join-artisan', { state: { background: location } });
  };

  const toggleCrafts = () => {
    setIsCraftsOpen(!isCraftsOpen);
    if (isCraftsOpen) setOpenCategory(null);
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <Transition show={isMenuOpen} as={Fragment}>
      {/* Overlay */}
      <Transition.Child
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-50"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-50"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black opacity-50 z-[60]"
          onClick={() => setIsMenuOpen(false)}
        ></div>
      </Transition.Child>

      {/* Sidebar */}
      <Transition.Child
        enter="transition transform duration-300"
        enterFrom="-translate-x-full"
        enterTo="translate-x-0"
        leave="transition transform duration-300"
        leaveFrom="translate-x-0"
        leaveTo="-translate-x-full"
      >
        <div className="fixed top-0 left-0 bottom-0  w-72 bg-white shadow-xl z-[70] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <LogoSection />
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 transition"
            >
              <X size={20} />
            </button>
          </div>

          {/* Menu items */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-3">
            {loggedIn && (
              <NavItem
                to="/feed"
                label="Explore"
                setIsMenuOpen={setIsMenuOpen}
              />
            )}
            {!loggedIn && (
              <NavItem to="/" label="Discover" setIsMenuOpen={setIsMenuOpen} />
            )}
            {(!loggedIn || userDetail?.active_role === 'user') && (
              <NavItem
                to="/artisans"
                label="Hire Artisans"
                setIsMenuOpen={setIsMenuOpen}
              />
            )}
            <div className="flex flex-col">
              <div className="flex items-center justify-between rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition">
                <NavLink
                  to="/categories"
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex-1 px-4 py-2 rounded-l-lg transition ${
                      isActive ? 'text-rose-600 bg-rose-50' : 'hover:text-rose-600'
                    }`
                  }
                >
                  Crafts
                </NavLink>
                <button
                  onClick={toggleCrafts}
                  className="px-4 py-2 hover:text-rose-600 transition-colors border-l border-gray-100"
                >
                  {isCraftsOpen ? (
                    <ChevronDown className="w-4 h-4 text-rose-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              </div>
              <Transition
                show={isCraftsOpen}
                enter="transition-all duration-300 ease-out overflow-hidden"
                enterFrom="opacity-0 max-h-0"
                enterTo="opacity-100 max-h-[2000px]"
                leave="transition-all duration-200 ease-in overflow-hidden"
                leaveFrom="opacity-100 max-h-[2000px]"
                leaveTo="opacity-0 max-h-0"
              >
                <div className="pl-4 pr-2 py-2 flex flex-col space-y-1">
                  {categorizedCrafts?.filter(cat => cat.crafts && cat.crafts.length > 0).map((category) => (
                    <div key={category.category_name} className="flex flex-col">
                      <div
                        className="flex items-center justify-between px-4 py-2 cursor-pointer rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-rose-600 transition"
                        onClick={() => setOpenCategory(openCategory === category.category_name ? null : category.category_name)}
                      >
                        <span>{category.category_name}</span>
                        {openCategory === category.category_name ? (
                          <ChevronDown className="w-3 h-3 text-rose-600" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                      </div>
                      <Transition
                        show={openCategory === category.category_name}
                        enter="transition-all duration-300 ease-out overflow-hidden"
                        enterFrom="opacity-0 max-h-0"
                        enterTo="opacity-100 max-h-[1000px]"
                        leave="transition-all duration-200 ease-in overflow-hidden"
                        leaveFrom="opacity-100 max-h-[1000px]"
                        leaveTo="opacity-0 max-h-0"
                      >
                        <div className="pl-4 flex flex-col space-y-1 mt-1 border-l border-gray-100 ml-4">
                          {category.crafts?.map((craft) => (
                            <NavLink
                              key={craft.id}
                              to={`/craft/${craft.id}`}
                              onClick={() => setIsMenuOpen(false)}
                              className={({ isActive }) =>
                                `block px-4 py-1.5 rounded-lg text-sm transition ${
                                  isActive
                                    ? 'bg-rose-50 text-rose-600 font-medium'
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-rose-600'
                                }`
                              }
                            >
                              {craft.name}
                            </NavLink>
                          ))}
                        </div>
                      </Transition>
                    </div>
                  ))}
                  <NavLink
                    to="/categories"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-2 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50 transition mt-1"
                  >
                    View All Categories &rarr;
                  </NavLink>
                </div>
              </Transition>
            </div>
            {loggedIn && (
              <NavItem
                to="/messages"
                label="Messages"
                setIsMenuOpen={setIsMenuOpen}
                badge={unreadMsgCount}
              />
            )}
            {!loggedIn && (
              <NavItem
                to="/how-it-works"
                label="Handpreneur Flow"
                setIsMenuOpen={setIsMenuOpen}
              />
            )}
            {loggedIn && userDetail?.active_role === 'artisan' && (
              <NavItem
                to="/artisan/dashboard"
                label="Dashboard"
                setIsMenuOpen={setIsMenuOpen}
              />
            )}
            <NavItem
              to="/support"
              label="Support"
              setIsMenuOpen={setIsMenuOpen}
            />
          </div>

          {/* Footer actions */}
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col gap-3">
            {loggedIn ? (
              isDualRole ? (
                <SwitchRoleToggle />
              ) : userDetail?.role === 'user' ? (
                <button
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-semibold rounded-lg shadow hover:shadow-lg transition"
                  onClick={handleUpgradeToArtisan}
                >
                  <Hammer size={16} /> Switch to Artisan
                </button>
              ) : (
                <div className="px-4 py-2 bg-amber-50 rounded-lg text-amber-700 font-medium flex items-center gap-2 shadow">
                  <Hammer size={16} /> Artisan
                </div>
              )
            ) : (
              <>
                <button
                  className="w-full px-4 py-2 bg-white text-rose-500 border border-rose-500  font-semibold rounded-lg hover:shadow-lg transition"
                  onClick={openSignup}
                >
                  Sign Up
                </button>
                <button
                  className="w-full px-4 py-2 bg-rose-500 text-white font-semibold rounded-lg hover:shadow-lg transition"
                  onClick={openJoinArtisan}
                >
                  Join as Artisan
                </button>
              </>
            )}
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );
}

function NavItem({ to, label, setIsMenuOpen, badge }) {
  return (
    <NavLink
      to={to}
      onClick={() => setIsMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center justify-between px-4 py-2 rounded-lg font-medium transition ${
          isActive
            ? 'bg-rose-100 text-rose-600'
            : 'text-gray-700 hover:bg-gray-100 hover:text-rose-600'
        }`
      }
    >
      <span>{label}</span>
      {badge > 0 && (
        <span className="bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] h-[20px] flex items-center justify-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}
